import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Percent, Gavel, Zap, Check, IdCard, Banknote, MessageCircle, Shield } from "lucide-react";

const ADMIN_WA = "6283830173704"; // 083830173704 international format
const WA_MESSAGE = encodeURIComponent(
  "Halo admin Ustore, saya ingin mendaftar menjadi seller. Mohon info tahapan selanjutnya. Terima kasih."
);

export default function BecomeSeller() {
  const { user, loading } = useAuth();

  if (loading) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role === "seller") return <Navigate to="/seller" replace />;

  const benefits = [
    { icon: Percent, title: "Fee Rendah Mulai 3%/produk", desc: "Fee per kategori, diatur transparan oleh admin. Lebih murah dari marketplace gaming lain." },
    { icon: Gavel, title: "Fitur Lelang Produk", desc: "Akses ke sistem auction profesional dengan BIN, anti-snipe, dan escrow otomatis." },
    { icon: Zap, title: "Sistem Sudah Otomatis", desc: "Pembayaran via Tripay langsung masuk ke wallet, transfer manual nol — fokus jual saja." },
  ];

  const requirements = [
    { icon: IdCard, title: "Foto Identitas", desc: "KTP / SIM / Paspor yang masih berlaku, foto jelas tidak buram." },
    { icon: Banknote, title: "Rekening atas Nama yang Sama", desc: "Nama di rekening bank/e-wallet harus sama dengan KTP untuk verifikasi." },
  ];

  return (
    <div data-testid="become-seller-page" className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16">
      {/* Hero */}
      <div className="mb-12">
        <span className="tag tag-accent">// UPGRADE ACCOUNT</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white mt-4">
          Jadi <span className="text-[#2563EB]">Seller</span> di Ustore
        </h1>
        <p className="text-white/60 text-base sm:text-lg mt-4 max-w-2xl leading-relaxed">
          Jual akun game, jasa joki, top-up, dan voucher digital ke ribuan gamer Indonesia.
          Sistem auto-escrow, fee transparan, payout langsung ke wallet.
        </p>
      </div>

      {/* Benefits */}
      <section className="mb-14">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center">
            <Check className="w-5 h-5 text-[#00FF66]" />
          </div>
          <h2 className="font-display font-black text-2xl uppercase tracking-wider text-white">Keuntungan</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="benefits-grid">
          {benefits.map((b, i) => (
            <div key={i} className="border border-white/10 hover:border-[#00FF66]/40 p-5 transition-colors" data-testid={`benefit-${i}`}>
              <b.icon className="w-7 h-7 text-[#00FF66] mb-3" />
              <h3 className="font-bold text-white text-base mb-2">{b.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements */}
      <section className="mb-14">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#FFD700]" />
          </div>
          <h2 className="font-display font-black text-2xl uppercase tracking-wider text-white">Syarat Pendaftaran</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4" data-testid="requirements-grid">
          {requirements.map((r, i) => (
            <div key={i} className="border border-white/10 p-5 flex gap-4" data-testid={`requirement-${i}`}>
              <r.icon className="w-8 h-8 text-[#FFD700] shrink-0" />
              <div>
                <h3 className="font-bold text-white text-base mb-1">{r.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-white/40 text-xs mt-4">
          Catatan: Dokumen tidak disimpan publik. Hanya untuk verifikasi admin sebelum aktivasi seller mode.
        </p>
      </section>

      {/* CTA — WhatsApp Admin */}
      <section className="border border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/10 to-transparent p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h2 className="font-display font-black text-2xl text-white mb-2">Siap Bergabung?</h2>
            <p className="text-white/70 text-sm leading-relaxed">
              Hubungi admin Ustore via WhatsApp untuk proses verifikasi.
              Sertakan <b className="text-white">foto identitas</b> dan <b className="text-white">data rekening</b> kamu.
              Setelah disetujui (biasanya 1×24 jam), akun kamu langsung di-upgrade.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-black border border-white/10">
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span className="text-sm text-white font-mono">083830173704</span>
              <span className="text-xs text-white/40 ml-2">(Admin Ustore)</span>
            </div>
          </div>
          <a
            data-testid="contact-admin-wa-btn"
            href={`https://wa.me/${ADMIN_WA}?text=${WA_MESSAGE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] hover:bg-[#1ebe5b] text-black font-display font-black uppercase tracking-[0.15em] text-sm transition-colors whitespace-nowrap"
          >
            <MessageCircle className="w-5 h-5" /> Daftar via WhatsApp
          </a>
        </div>
      </section>

      <p className="text-center text-white/40 text-xs mt-8 uppercase tracking-widest">
        // Ustore × Undeadshopp · Verifikasi manual untuk keamanan komunitas
      </p>
    </div>
  );
}
